import React, { useState } from "react";
import { Container, Button } from "reactstrap";
import { utils } from "../../../utils/utils";
import SequencesTable from "./SequencesTable";
import { useAuth } from "../../../providers/authProvider";
import InformationModal from "../../InformationModal";
import ConfirmationModal from "../../ConfirmationModal";
import SequenceDetailsModal from "./SequenceDetailsModal";
import { sequenceApi } from "../../../services/sequenceServices";

const WAITING_FOR_CHECKING_STATUS = 4;
const CHECKING_IN_PROGRESS_STATUS = 5;
const WAITING_FOR_CHECKING_TAB = "WAITING_FOR_CHECKING_TAB";

const WaitingForChecking = ({
  sequencesTotal,
  setSequencesTotal,
  reloadTabs,
  setReloadTabs,
  tabName,
  search = "",
}) => {
  const [authContext] = useAuth();
  const userId = authContext?.currentUser?.id;
  const [checkerId, setCheckerId] = useState();
  const [detailerId, setDetailerId] = useState();
  const [detailModal, setDetailModal] = useState(false);
  const [sequenceSelected, setSequenceSelected] = useState();
  const [sequenceStatusId, setSequenceStatusId] = useState();
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal
  );

  const columns = [
    {
      accessor: "sendToCheckingButton",
      Header: "",
      headerProps: { className: "text-truncate" },
      disableSortBy: true,
      width: 130,
      Cell: (rowData) => {
        const sequence = rowData.row.original;
        const canPerform =
          utils.canUserPerformDetailingAction(
            sequence,
            WAITING_FOR_CHECKING_STATUS,
            userId
          ) || utils.isAdminRole(authContext.currentUser?.role?.id);
        if (!canPerform) return null;
        return (
          <Button
            className="btn-sm rounded px-1 py-0"
            color={"success"}
            onClick={() => {
              const sequence = rowData.row.original;
              changeSequenceStatus(
                sequence,
                CHECKING_IN_PROGRESS_STATUS,
                sequence?.detailer?.id ?? null,
                sequence?.checker?.id ?? null
              );
            }}
          >
            Start Checking
          </Button>
        );
      },
    },
    {
      id: "seeJobDetailsButton",
      accessor: "seeJobDetailsButton",
      Header: "",
      headerProps: { className: "text-truncate" },
      disableSortBy: true,
      width: 125,
      Cell: (rowData) => {
        return (
          <Button
            className="btn-sm rounded px-1 py-0"
            color={"primary"}
            onClick={() => {
              const sequence = rowData.row.original;
              setSequenceSelected(sequence);
              setDetailerId(sequence?.detailer?.id || null);
              setCheckerId(sequence?.checker?.id || null);
              setSequenceStatusId(sequence.sequenceStatusId);
              setDetailModal(true);
            }}
          >
            See Job Details
          </Button>
        );
      },
    },
  ];

  const changeSequenceStatus = async (
    sequence,
    sequenceStatusId,
    effectiveDetailerId = detailerId,
    effectiveCheckerId = checkerId
  ) => {
    try {
      const startChecking = sequenceStatusId === CHECKING_IN_PROGRESS_STATUS;
      onClear();
      setConfirmationModal({
        isOpen: true,
        onSubmit: async () => {
          try {
            setReloadTabs([]);
            const { detailer, checker, ...sequenceFields } = sequence;
            await sequenceApi.updateSequence(sequence.id, {
              ...sequenceFields,
              detailerId: effectiveDetailerId,
              checkerId: effectiveCheckerId,
              sequenceStatusId,
            });
            onClear();
            setInformationModal({
              isOpen: true,
              title: startChecking ? "Start Checking" : "Sequence Update",
              body: startChecking
                ? "Checking started successfully"
                : "Sequence updated successfully.",
            });
            setReloadTabs([
              WAITING_FOR_CHECKING_TAB,
              utils.detailingTabs[sequenceStatusId],
            ]);
          } catch (error) {
            onClear();
            setInformationModal({
              isOpen: true,
              title: "Error",
              body: "There was an error with your request.",
            });
          }
        },
        onClose: () => onClear(),
        title: startChecking ? "Start Checking" : "Sequence Update",
        body: startChecking
          ? `
          <div class="text-center">
            Are you sure you are ready to begin checking this job?
          </div>
        `
          : `
        <div class="text-center">
          Do you confirm you want to update the sequence ${sequence.job.jobNumber}?
        </div>
      `,
        confirmColor: "warning",
      });
    } catch (error) {
      onClear();
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error with your request.",
      });
    }
  };

  const onClear = async () => {
    setDetailerId();
    setCheckerId();
    setSequenceSelected();
    setDetailModal(false);
    setSequenceStatusId();
    setConfirmationModal(utils.initConfirmationModal);
    setInformationModal(utils.initInformationModal);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    changeSequenceStatus(
      sequenceSelected,
      sequenceStatusId,
      detailerId,
      checkerId
    );
  };

  return (
    <Container fluid className="px-0">
      <SequencesTable
        columns={columns}
        sequenceStatusId={WAITING_FOR_CHECKING_STATUS}
        setSequencesTotal={setSequencesTotal}
        sequencesTotal={sequencesTotal}
        reloadTabs={reloadTabs}
        tabName={tabName}
        search={search}
      />
      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() => setInformationModal(utils.initInformationModal)}
        />
      ) : confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : detailModal ? (
        <SequenceDetailsModal
          sequence={sequenceSelected}
          detailModal={detailModal}
          onSubmit={onSubmit}
          onClear={onClear}
          checkerId={checkerId}
          setCheckerId={setCheckerId}
          detailerId={detailerId}
          setDetailerId={setDetailerId}
          enableUpdate={
            utils.canUserPerformDetailingAction(
              sequenceSelected,
              sequenceStatusId,
              userId
            ) || utils.isAdminRole(authContext.currentUser?.role?.id)
          }
          statusId={sequenceStatusId}
          setStatusId={setSequenceStatusId}
        />
      ) : null}
    </Container>
  );
};

export default WaitingForChecking;
